/**
 * Textarea Component
 * 
 * Multi-line text input built on MUI TextField.
 * Provides consistent styling with HAAT brand colors.
 * 
 * @example
 * // Basic
 * <Textarea label="Description" />
 * 
 * @example
 * // With error
 * <Textarea label="Notes" error="This field is required" />
 * 
 * @example
 * // Custom rows
 * <Textarea label="Content" rows={6} minRows={3} maxRows={10} />
 */

import React, { forwardRef } from 'react';
import TextField from '@mui/material/TextField';
import { SxProps, Theme } from '@mui/material/styles';

// =============================================================================
// TYPES
// =============================================================================

export type TextareaSize = 'small' | 'medium' | 'large';
export type TextareaVariant = 'outlined' | 'filled' | 'standard';

export interface TextareaProps {
  /** Label text */
  label?: string;
  /** Hide label (still used for accessibility) */
  hideLabel?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Current value */
  value?: string;
  /** Default value (uncontrolled) */
  defaultValue?: string;
  /** Change handler */
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /** Blur handler */
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  /** Focus handler */
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  /** Error message (shows error state when provided) */
  error?: string;
  /** Helper text displayed below textarea */
  helperText?: string;
  /** Required field marker */
  required?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Read-only state */
  readOnly?: boolean;
  /** Number of visible rows */
  rows?: number;
  /** Minimum rows (for auto-grow) */
  minRows?: number;
  /** Maximum rows (limits auto-grow) */
  maxRows?: number;
  /** Size variant */
  size?: TextareaSize;
  /** Visual variant */
  variant?: TextareaVariant;
  /** Full width */
  fullWidth?: boolean;
  /** Apply rounded corners */
  rounded?: boolean;
  /** Custom width */
  width?: string | number;
  /** Additional class name */
  className?: string;
  /** Custom MUI sx styles */
  sx?: SxProps<Theme>;
  /** Name attribute */
  name?: string;
  /** ID attribute */
  id?: string;
  /** Maximum character length */
  maxLength?: number;
  /** Auto focus on mount */
  autoFocus?: boolean;
  /** Text direction (ltr/rtl) */
  dir?: 'ltr' | 'rtl';
}

// =============================================================================
// SIZE MAPPING
// =============================================================================

const sizeConfig: Record<TextareaSize, { muiSize: 'small' | 'medium'; fontSize: string }> = {
  small: { muiSize: 'small', fontSize: '0.875rem' },
  medium: { muiSize: 'medium', fontSize: '1rem' },
  large: { muiSize: 'medium', fontSize: '1.125rem' },
};

// =============================================================================
// COMPONENT
// =============================================================================

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      // Label & text
      label,
      hideLabel = false,
      placeholder,
      helperText,

      // Value & handlers
      value,
      defaultValue,
      onChange,
      onBlur,
      onFocus,

      // Validation
      error,
      required = false,

      // State
      disabled = false,
      readOnly = false,

      // Rows configuration
      rows = 4,
      minRows,
      maxRows,

      // Styling
      size = 'medium',
      variant = 'outlined',
      fullWidth = true,
      rounded = true,
      width,
      className,
      sx,

      // Other
      name,
      id,
      maxLength,
      autoFocus,
      dir,
    },
    ref
  ) => {
    // =========================================================================
    // COMPUTED VALUES
    // =========================================================================

    const hasError = !!error;
    const displayHelperText = error || helperText;
    const { muiSize, fontSize } = sizeConfig[size];

    // =========================================================================
    // CUSTOM STYLES
    // =========================================================================

    const customStyles: SxProps<Theme> = {
      width: width || (fullWidth ? '100%' : 'auto'),

      // Root styles
      '& .MuiOutlinedInput-root': {
        borderRadius: rounded ? '8px' : '4px',
        padding: 0,
        backgroundColor: variant === 'filled' ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
        transition: 'all 0.2s ease-in-out',

        '& fieldset': {
          borderColor: hasError ? '#c00035' : '#e5e7eb',
          transition: 'border-color 0.2s ease-in-out',
        },

        '&:hover fieldset': {
          borderColor: hasError ? '#c00035' : '#9ca3af',
        },

        '&.Mui-focused fieldset': {
          borderColor: hasError ? '#c00035' : 'rgb(var(--color-primary-500))',
          borderWidth: '2px',
        },

        '&.Mui-disabled': {
          backgroundColor: '#f9fafb',
          '& fieldset': {
            borderColor: '#e5e7eb',
          },
        },
      },

      // Label styles
      '& .MuiInputLabel-root': {
        color: hasError ? '#c00035' : '#6b7280',

        '&.Mui-focused': {
          color: hasError ? '#c00035' : 'rgb(var(--color-primary-500))',
        },
      },

      // Input styles
      '& .MuiInputBase-input': {
        fontSize,

        '&::placeholder': {
          color: '#9ca3af',
          opacity: 1,
        },
      },

      // Textarea specific - match single-line Input padding
      '& .MuiInputBase-inputMultiline': {
        padding: '16.5px 14px',
        resize: 'none',
      },

      // Helper text styles
      '& .MuiFormHelperText-root': {
        marginLeft: '2px',
        marginTop: '6px',
        fontSize: '0.75rem',

        '&.Mui-error': {
          color: '#c00035',
        },
      },

      // Filled variant specific
      ...(variant === 'filled' && {
        '& .MuiFilledInput-root': {
          borderRadius: rounded ? '8px 8px 0 0' : '4px 4px 0 0',
          backgroundColor: 'rgba(0, 0, 0, 0.04)',

          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
          },

          '&.Mui-focused': {
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
          },
        },
      }),

      // Standard variant specific
      ...(variant === 'standard' && {
        '& .MuiInput-underline': {
          '&:before': {
            borderBottomColor: hasError ? '#c00035' : '#e5e7eb',
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottomColor: hasError ? '#c00035' : '#9ca3af',
          },
          '&:after': {
            borderBottomColor: hasError ? '#c00035' : 'rgb(var(--color-primary-500))',
          },
        },
      }),

      // Merge with custom sx
      ...sx,
    };

    // =========================================================================
    // RENDER
    // =========================================================================

    return (
      <TextField
        inputRef={ref}
        variant={variant}
        size={muiSize}
        fullWidth={fullWidth}
        label={hideLabel ? undefined : label}
        placeholder={placeholder}
        helperText={displayHelperText}
        error={hasError}
        required={required}
        multiline
        rows={minRows || maxRows ? undefined : rows}
        minRows={minRows}
        maxRows={maxRows}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={disabled}
        name={name}
        id={id}
        autoFocus={autoFocus}
        className={className}
        sx={customStyles}
        InputProps={{
          readOnly,
          'aria-label': hideLabel ? label : undefined,
        }}
        inputProps={{
          maxLength,
          dir,
        }}
      />
    );
  }
);

// Display name for debugging
Textarea.displayName = 'Textarea';

export default Textarea;
